import { StyleSheet, css } from 'aphrodite'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import ConfirmationDialog from '../components/ConfirmationDialog'
import SkeletonLoader from '../components/SkeletonLoader'
import StatusChip from '../components/StatusChip'
import BrokerInvoiceItemsTable from '../components/invoice/BrokerInvoiceItemsTable'
import DriverInvoiceItemsTable from '../components/invoice/DriverInvoiceItemsTable'
import InvoiceDisplay from '../components/invoice/InvoiceDisplay'
import { useNotificationContext } from '../contexts/NotificationContext'
import { useTagContext } from '../contexts/TagContext'
import { useUser } from '../contexts/UserContext'
import { invoiceService } from '../services/invoiceService'
import { loadService } from '../services/loadService'
import { projectService } from '../services/projectService'
import { truckService } from '../services/truckService'
import { userService } from '../services/userService'
import { workOrderService } from '../services/workOrderService'
import type {
  BrokerInvoiceItem,
  DriverInvoiceItem,
  Invoice,
  InvoiceItem,
} from '../types/invoice'
import { isBrokerInvoiceItem, isDriverInvoiceItem } from '../types/invoice'
import type { Tag } from '../types/tag'
import type { WorkOrder } from '../types/workorder'

// Type for API response item
interface ApiInvoiceItem extends Omit<InvoiceItem, 'tag_id' | 'workorder_id'> {
  tag?: number
  workorder?: number
}

// Type for API response
interface ApiInvoice extends Omit<Invoice, 'items'> {
  items: ApiInvoiceItem[]
  invoice_type: 'BROKER' | 'DRIVER'
}

const styles = StyleSheet.create({
  container: {
    padding: '24px',
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
    overflowX: 'hidden',
  },
  invoiceContainer: {
    backgroundColor: 'white',
    padding: '40px',
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '0px',
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    maxWidth: '90%',
    margin: '0 auto',
    overflowX: 'auto',
    '@media (max-width: 1024px)': {
      padding: '16px',
      maxWidth: '100%',
    },
  },
  skeletonLoader: {
    margin: 0,
    border: 'none',
    borderRadius: '8px',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '16px',
    marginTop: '24px',
    maxWidth: '90%',
    margin: '24px auto 0 auto',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      maxWidth: '100%',
      padding: '0 16px',
    },
  },
  downloadButton: {
    backgroundColor: '#10B981',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ':hover': {
      backgroundColor: '#059669',
    },
    ':disabled': {
      backgroundColor: '#6EE7B7',
      cursor: 'not-allowed',
    },
  },
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: '90%',
    margin: '0 auto 16px auto',
    padding: '12px 16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  statusLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  disputeBanner: {
    maxWidth: '90%',
    margin: '0 auto 16px auto',
    padding: '16px',
    backgroundColor: '#FFFBEB',
    border: '1px solid #FDE68A',
    borderRadius: '8px',
  },
  disputeBannerHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    color: '#92400E',
    fontSize: '14px',
  },
  disputeReason: {
    margin: 0,
    fontSize: '14px',
    color: '#78350F',
    lineHeight: '1.5',
  },
  editButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  disputeButton: {
    backgroundColor: '#F59E0B',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ':hover': {
      backgroundColor: '#D97706',
    },
  },
  clearDisputeButton: {
    backgroundColor: '#3B82F6',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ':hover': {
      backgroundColor: '#2563EB',
    },
    ':disabled': {
      backgroundColor: '#93C5FD',
      cursor: 'not-allowed',
    },
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1200,
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 20px 0 20px',
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
  },
  modalCloseButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    color: '#6B7280',
    padding: '4px',
    ':hover': {
      color: '#111827',
    },
  },
  modalBody: {
    padding: '16px 20px',
  },
  modalDescription: {
    margin: '0 0 12px 0',
    fontSize: '14px',
    color: '#6B7280',
    lineHeight: '1.5',
  },
  disputeTextarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '6px',
    border: '1px solid #D1D5DB',
    fontSize: '14px',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    ':focus': {
      outline: 'none',
      borderColor: '#3B82F6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
  },
  charCount: {
    textAlign: 'right',
    fontSize: '12px',
    color: '#9CA3AF',
    marginTop: '4px',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid #E5E7EB',
  },
  modalCancelButton: {
    backgroundColor: '#F3F4F6',
    color: '#374151',
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#E5E7EB',
    },
  },
  modalConfirmButton: {
    backgroundColor: '#F59E0B',
    color: 'white',
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    ':hover': {
      backgroundColor: '#D97706',
    },
    ':disabled': {
      backgroundColor: '#FCD34D',
      cursor: 'not-allowed',
      color: '#A16207',
    },
  },
})

const ViewInvoicePage: React.FC = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [contractorNames, setContractorNames] = useState<
    Record<number, string>
  >({})
  const [brokerNames, setBrokerNames] = useState<Record<number, string>>({})
  const { user } = useUser()
  const { tags, refreshTags } = useTagContext()
  const tagsRef = useRef(tags)
  const refreshTagsRef = useRef(refreshTags)
  const [userTruckNumber, setUserTruckNumber] = useState<string>('')
  const userTruckNumberRef = useRef<string>('')
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [showDisputeModal, setShowDisputeModal] = useState(false)
  const [disputeReason, setDisputeReason] = useState('')
  const [isDisputing, setIsDisputing] = useState(false)
  const [isClearingDispute, setIsClearingDispute] = useState(false)
  const { invoices: invoiceNotifications } = useNotificationContext()

  // Update ref when tags change
  useEffect(() => {
    tagsRef.current = tags
  }, [tags])

  useEffect(() => {
    refreshTagsRef.current = refreshTags
  }, [refreshTags])

  useEffect(() => {
    userTruckNumberRef.current = userTruckNumber
  }, [userTruckNumber])

  // Fetch user's truck number
  useEffect(() => {
    const fetchUserTruck = async () => {
      if (user?.id) {
        try {
          const trucks = await truckService.getUserVehicles(user.id)
          if (trucks && trucks.length > 0) {
            setUserTruckNumber(trucks[0].truck_number)
          }
        } catch (error) {
          console.error('Failed to fetch truck info:', error)
        }
      }
    }
    fetchUserTruck()
  }, [user])

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setIsLoading(true)
        const data = (await invoiceService.get(Number(id))) as ApiInvoice
        // Transform the items to match our type definitions
        const transformedData = {
          ...data,
          items: await Promise.all(
            data.items.map(async (item) => {
              if (
                data.invoice_type === 'DRIVER' &&
                'tag' in item &&
                item.tag !== undefined
              ) {
                // Find the tag to get assigned user info
                const tag = tagsRef.current.find((t: Tag) => t.id === item.tag)
                let truckNumber = userTruckNumberRef.current // fallback
                let weightData = 0

                if (tag) {
                  // Get truck number from assigned user
                  if (tag.assigned_user?.id) {
                    try {
                      const userVehicles = await truckService.getUserVehicles(
                        tag.assigned_user.id,
                      )
                      if (userVehicles.length > 0) {
                        truckNumber = userVehicles[0].truck_number
                      }
                    } catch (error) {
                      console.error(
                        'Failed to fetch assigned user truck info:',
                        error,
                      )
                    }
                  }

                  // Get weight data from loads
                  try {
                    const loads = await loadService.getLoadsByTag(
                      tag.id.toString(),
                    )
                    weightData = loads.reduce(
                      (sum, load) => sum + (Number(load.weight) || 0),
                      0,
                    )
                  } catch (error) {
                    console.error('Failed to fetch load weight data:', error)
                  }
                }

                return {
                  ...item,
                  tag_id: item.tag,
                  tag: undefined,
                  workorder_id: undefined,
                  truck_number: truckNumber,
                  volume: weightData,
                } as DriverInvoiceItem
              } else if (
                data.invoice_type === 'BROKER' &&
                'tag' in item &&
                item.tag !== undefined
              ) {
                return {
                  ...item,
                  tag_id: item.tag,
                  workorder: undefined,
                  truck_number: undefined,
                } as BrokerInvoiceItem
              }
              return item as InvoiceItem
            }),
          ),
        }
        setInvoice(transformedData)

        // Mark invoice as read if viewer is the bill_to user
        if (user?.id && transformedData.bill_to?.id === user.id) {
          invoiceNotifications.markAsRead(Number(id))
        }

        // No need to fetch broker tags separately - they'll be available in the tags context

        // Only refresh tags if we don't have any yet
        if (tagsRef.current.length === 0) {
          await refreshTagsRef.current()
        }

        // Fetch contractor and broker info for each item
        const contractorNamesMap: Record<number, string> = {}
        const brokerNamesMap: Record<number, string> = {}

        for (const item of transformedData.items) {
          if (transformedData.invoice_type === 'DRIVER') {
            const driverItem = item as DriverInvoiceItem
            if (!driverItem.tag_id) continue
            try {
              const tag = tagsRef.current.find(
                (t: Tag) => t.id === driverItem.tag_id,
              )
              if (!tag) continue
              // Normalize project id from either tag.workorder or tag.project
              const extractProjectId = (t: Tag): number | null => {
                const maybeNum = (val: unknown): number | null => {
                  if (typeof val === 'number') return val
                  if (typeof val === 'string') {
                    const n = Number.parseInt(val, 10)
                    return Number.isFinite(n) ? n : null
                  }
                  return null
                }

                // Try workorder first
                const wo = (t as unknown as { workorder?: unknown }).workorder
                if (wo && typeof wo === 'object') {
                  const proj = (wo as { project?: unknown }).project
                  const idFromProjObj =
                    proj && typeof proj === 'object'
                      ? maybeNum((proj as { id?: unknown }).id)
                      : maybeNum(proj)
                  if (idFromProjObj != null) return idFromProjObj
                } else {
                  const idFromWo = maybeNum(wo)
                  if (idFromWo != null) return idFromWo
                }

                // Fallback to tag.project
                const tp = (t as unknown as { project?: unknown }).project
                if (tp && typeof tp === 'object') {
                  return maybeNum((tp as { id?: unknown }).id)
                }
                return maybeNum(tp)
              }

              const projectId = extractProjectId(tag)
              // Normalize workorder id for dispatcher lookup
              const extractWorkorderId = (t: Tag): number | null => {
                const maybeNum = (val: unknown): number | null => {
                  if (typeof val === 'number') return val
                  if (typeof val === 'string') {
                    const n = Number.parseInt(val, 10)
                    return Number.isFinite(n) ? n : null
                  }
                  return null
                }
                const wo = (t as unknown as { workorder?: unknown }).workorder
                if (wo && typeof wo === 'object') {
                  return maybeNum((wo as { id?: unknown }).id)
                }
                return maybeNum(wo)
              }
              const workorderId = extractWorkorderId(tag)

              if (projectId) {
                // Get contractor info
                const contractor =
                  await projectService.getProjectContractor(projectId)
                if (contractor) {
                  contractorNamesMap[tag.id] = contractor.name
                } else {
                  contractorNamesMap[tag.id] = '-'
                }

                // Get broker info from workorder
                if (workorderId != null) {
                  // First try to get broker directly from the invoice item
                  if (driverItem.broker) {
                    try {
                      const broker = await userService.getUserById(
                        driverItem.broker,
                      )
                      brokerNamesMap[tag.id] =
                        `${broker.first_name} ${broker.last_name}`.trim()
                      continue
                    } catch (error) {
                      console.error('Failed to fetch broker directly:', error)
                    }
                  }

                  // Fall back to getting broker from workorder dispatchers
                  const workorderData =
                    await workOrderService.getWorkOrder(workorderId)
                  if (
                    workorderData.dispatchers &&
                    workorderData.dispatchers.length > 0
                  ) {
                    // Use the first dispatcher as the broker
                    const dispatcher = workorderData.dispatchers[0]
                    const brokerName =
                      `${dispatcher.first_name} ${dispatcher.last_name}`.trim()
                    brokerNamesMap[tag.id] =
                      brokerName || dispatcher.email || '-'
                  } else {
                    brokerNamesMap[tag.id] = '-'
                  }
                } else {
                  brokerNamesMap[tag.id] = '-'
                }
              }
            } catch (error) {
              console.error('Failed to fetch contractor/broker info:', error)
              contractorNamesMap[driverItem.tag_id] = '-'
              brokerNamesMap[driverItem.tag_id] = '-'
            }
          } else if (transformedData.invoice_type === 'BROKER') {
            const brokerItem = item as BrokerInvoiceItem
            if (!brokerItem.tag_id || !brokerItem.contractor) continue

            try {
              const contractor = await userService.getUserById(
                brokerItem.contractor,
              )
              if (contractor?.first_name && contractor?.last_name) {
                // Store contractor name with tag_id as key (not contractor user ID)
                contractorNamesMap[brokerItem.tag_id] =
                  `${contractor.first_name} ${contractor.last_name}`
                console.log(
                  `Contractor for tag ${brokerItem.tag_id}:`,
                  `${contractor.first_name} ${contractor.last_name}`,
                )
              } else {
                contractorNamesMap[brokerItem.tag_id] = '-'
              }
            } catch (error) {
              console.error('Failed to fetch contractor info:', error)
              contractorNamesMap[brokerItem.tag_id] = '-'
            }
          }
        }
        console.log('Final contractorNamesMap:', contractorNamesMap)
        console.log('Final brokerNamesMap:', brokerNamesMap)
        setContractorNames(contractorNamesMap)
        setBrokerNames(brokerNamesMap)
      } catch (error) {
        console.error('ViewInvoicePage - Error fetching invoice:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchInvoice()
  }, [id, user?.id, invoiceNotifications])

  const handleDownload = async () => {
    if (!invoice?.id) return

    setIsDownloading(true)
    try {
      const pdfBlob = await invoiceService.downloadPdf(invoice.id)
      const url = window.URL.createObjectURL(pdfBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `invoice-${invoice.id}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Invoice downloaded successfully!')
    } catch (error) {
      console.error('Error downloading PDF:', error)
      toast.error('Failed to download PDF. Please try again.', {
        style: { color: '#721c24' },
      })
    } finally {
      setIsDownloading(false)
    }
  }

  const handleDownloadCsv = async () => {
    if (!invoice?.id) return

    setIsDownloadingCsv(true)
    try {
      const csvBlob = await invoiceService.downloadCsv(invoice.id)
      const url = window.URL.createObjectURL(csvBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `invoice-${invoice.id}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Invoice downloaded successfully!')
    } catch (error) {
      console.error('Error downloading CSV:', error)
      toast.error('Failed to download CSV. Please try again.', {
        style: { color: '#721c24' },
      })
    } finally {
      setIsDownloadingCsv(false)
    }
  }

  const handleDispute = async () => {
    if (!invoice?.id || !disputeReason.trim()) return
    setIsDisputing(true)
    try {
      const updated = await invoiceService.disputeInvoice(
        invoice.id,
        disputeReason.trim(),
      )
      setInvoice((prev) => (prev ? { ...prev, ...updated } : prev))
      setShowDisputeModal(false)
      setDisputeReason('')
      toast.success('Invoice disputed successfully')
    } catch (error) {
      console.error('Failed to dispute invoice:', error)
      toast.error('Failed to dispute invoice')
    } finally {
      setIsDisputing(false)
    }
  }

  const handleClearDispute = async () => {
    if (!invoice?.id) return
    setIsClearingDispute(true)
    try {
      const updated = await invoiceService.clearDispute(invoice.id)
      setInvoice((prev) => (prev ? { ...prev, ...updated } : prev))
      toast.success('Dispute cleared successfully')
    } catch (error) {
      console.error('Failed to clear dispute:', error)
      toast.error('Failed to clear dispute')
    } finally {
      setIsClearingDispute(false)
    }
  }

  // Determine user roles relative to this invoice
  const isBillToUser = user?.id === invoice?.bill_to?.id
  const isCreator = user?.id === invoice?.created_by?.id

  if (isLoading || !invoice) {
    return (
      <div className={css(styles.container)}>
        <div className={css(styles.invoiceContainer)}>
          <SkeletonLoader
            rows={8}
            columns={7}
            showHeader={true}
            className={css(styles.skeletonLoader)}
          />
        </div>
      </div>
    )
  }

  const logoUrl = user?.logo_url || invoice.logo || undefined

  const statusDisplay = (() => {
    if (
      invoice.status === 'UNPAID' &&
      invoice.due_date &&
      new Date(invoice.due_date) < new Date()
    ) {
      return { label: 'OVERDUE', variant: 'overdue' as const }
    }
    return {
      label: invoice.status,
      variant: invoice.status.toLowerCase() as
        | 'unpaid'
        | 'paid'
        | 'canceled'
        | 'disputed',
    }
  })()

  return (
    <div className={css(styles.container)}>
      {/* Status bar */}
      <div className={css(styles.statusBar)}>
        <div className={css(styles.statusLeft)}>
          <span className={css(styles.statusLabel)}>Status:</span>
          <StatusChip variant={statusDisplay.variant}>
            {statusDisplay.label}
          </StatusChip>
        </div>
      </div>

      {/* Dispute info banner */}
      {invoice.status === 'DISPUTED' && invoice.dispute_reason && (
        <div className={css(styles.disputeBanner)}>
          <div className={css(styles.disputeBannerHeader)}>
            <i
              className="fa-solid fa-triangle-exclamation"
              style={{ color: '#92400E' }}
            />
            <strong>This invoice has been disputed</strong>
          </div>
          <p className={css(styles.disputeReason)}>
            Reason: {invoice.dispute_reason}
          </p>
        </div>
      )}

      <InvoiceDisplay invoice={invoice} logoUrl={logoUrl} />
      <div className={css(styles.invoiceContainer)}>
        {invoice.invoice_type === 'DRIVER' ? (
          <DriverInvoiceItemsTable
            items={invoice.items as DriverInvoiceItem[]}
            isEditable={false}
            contractorNames={contractorNames}
            brokerNames={brokerNames}
            userTruckNumber={userTruckNumber}
            tags={tags}
          />
        ) : (
          <BrokerInvoiceItemsTable
            items={invoice.items as BrokerInvoiceItem[]}
            isEditable={false}
            contractorNames={contractorNames}
            tags={tags}
          />
        )}
      </div>
      <div className={css(styles.actions)}>
        {/* Edit button - shown to creator when UNPAID */}
        {isCreator && invoice.status === 'UNPAID' && (
          <button
            className={css(styles.editButton)}
            onClick={() => navigate(`/invoices/edit/${invoice.id}`)}
          >
            <i className="fa-solid fa-pen" /> Edit Invoice
          </button>
        )}

        {/* Dispute button - shown to bill_to user when UNPAID */}
        {isBillToUser && invoice.status === 'UNPAID' && (
          <button
            className={css(styles.disputeButton)}
            onClick={() => setShowDisputeModal(true)}
          >
            <i className="fa-solid fa-flag" /> Dispute Invoice
          </button>
        )}

        {/* Clear dispute button - shown to creator when DISPUTED */}
        {isCreator && invoice.status === 'DISPUTED' && (
          <button
            className={css(styles.clearDisputeButton)}
            onClick={handleClearDispute}
            disabled={isClearingDispute}
          >
            {isClearingDispute ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" /> Clearing...
              </>
            ) : (
              <>
                <i className="fa-solid fa-check-circle" /> Clear Dispute
              </>
            )}
          </button>
        )}

        <button
          className={css(styles.downloadButton)}
          onClick={handleDownload}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" /> Downloading...
            </>
          ) : (
            <>
              <i className="fa-solid fa-download" /> Download PDF
            </>
          )}
        </button>

        <button
          className={css(styles.downloadButton)}
          onClick={handleDownloadCsv}
          disabled={isDownloadingCsv}
        >
          {isDownloadingCsv ? (
            <>
              <i className="fa-solid fa-spinner fa-spin" /> Downloading...
            </>
          ) : (
            <>
              <i className="fa-solid fa-file-csv" /> Download CSV
            </>
          )}
        </button>
      </div>

      {/* Dispute modal */}
      {showDisputeModal && (
        <div
          className={css(styles.modalOverlay)}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDisputeModal(false)
              setDisputeReason('')
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setShowDisputeModal(false)
              setDisputeReason('')
            }
          }}
        >
          <div className={css(styles.modalContent)}>
            <div className={css(styles.modalHeader)}>
              <h3 className={css(styles.modalTitle)}>Dispute Invoice</h3>
              <button
                className={css(styles.modalCloseButton)}
                onClick={() => {
                  setShowDisputeModal(false)
                  setDisputeReason('')
                }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className={css(styles.modalBody)}>
              <p className={css(styles.modalDescription)}>
                Please provide a reason for disputing invoice #
                {invoice.invoice_number}. The invoice creator will be notified.
              </p>
              <textarea
                className={css(styles.disputeTextarea)}
                placeholder="Enter dispute reason..."
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                rows={4}
                maxLength={1000}
              />
              <div className={css(styles.charCount)}>
                {disputeReason.length}/1000
              </div>
            </div>
            <div className={css(styles.modalFooter)}>
              <button
                className={css(styles.modalCancelButton)}
                onClick={() => {
                  setShowDisputeModal(false)
                  setDisputeReason('')
                }}
              >
                Cancel
              </button>
              <button
                className={css(styles.modalConfirmButton)}
                onClick={handleDispute}
                disabled={!disputeReason.trim() || isDisputing}
              >
                {isDisputing ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" /> Submitting...
                  </>
                ) : (
                  'Submit Dispute'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ViewInvoicePage
